import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  FC,
  ReactNode,
} from "react";
import {
  IntakeFormData,
  IntakeFormUserInputFields,
  CompleteIntakeFormData,
  CounselingIntent,
  GisIntakeStep,
  GIS_INTAKE_STEPS,
  GIS_INTAKE_STEPS_NO_ORDER,
  getStepNumber as getStepNumberForSteps,
  getNextStep as getNextStepForSteps,
  getPreviousStep as getPreviousStepForSteps,
} from "@app/service/intakeForm";

/**
 * Context value type for intake form state management
 */
interface IntakeFormContextValue {
  /** Pre-filled data from backend */
  prefilledData: IntakeFormData | null;
  /** User input fields that need to be filled during the flow */
  userInputFields: IntakeFormUserInputFields;
  /** Current step enum value */
  currentStep: GisIntakeStep;
  /** Ordered list of steps for the active flow (standard vs no-order) */
  steps: GisIntakeStep[];
  /** Total number of steps in the active flow */
  totalSteps: number;
  /** Server-side OncUppGisTriage id when persisted (row is created only after successful GIS booking) */
  sessionId: number | null;
  /** User-confirmed phone from step 1 (survives navigation) */
  confirmedPhone: string | null;
  /** User-confirmed email from step 1 (survives navigation) */
  confirmedEmail: string | null;
  /** GIS flow type determined by the triage rule engine (e.g. "WH_EMPOWER" or "ONC") */
  triageGisFlowType: string | null;
  /**
   * Counseling intent derived from the selected order's test status at intake
   * entry. Available throughout the intake steps and at Acuity submission so
   * we can route pre-test vs post-test sessions consistently.
   */
  counselingIntent: CounselingIntent | null;
  /**
   * True once the patient has successfully booked the GIS appointment. The
   * intake answers are locked at this point — the flow's guards use this to
   * stop the patient from navigating back into the (now read-only) intake
   * steps and editing answers behind a confirmed booking.
   */
  bookingConfirmed: boolean;
  /** Set the pre-filled data from backend */
  setPrefilledData: (data: IntakeFormData | null) => void;
  /**
   * Update a single pre-filled field in-session. Used by the no-order flow to
   * capture the test type / cancer type / cancer stage that a backing order
   * would otherwise supply.
   */
  updatePrefilledField: <K extends keyof IntakeFormData>(
    field: K,
    value: IntakeFormData[K]
  ) => void;
  /** Returns the 1-based step number within the active flow */
  getStepNumber: (step: GisIntakeStep) => number;
  /** Returns the next step in the active flow, or null if last */
  getNextStep: (step: GisIntakeStep) => GisIntakeStep | null;
  /** Returns the previous step in the active flow, or null if first */
  getPreviousStep: (step: GisIntakeStep) => GisIntakeStep | null;
  /** Update a specific user input field */
  updateUserField: <K extends keyof IntakeFormUserInputFields>(
    field: K,
    value: IntakeFormUserInputFields[K]
  ) => void;
  /** Update multiple user input fields at once */
  updateUserFields: (fields: Partial<IntakeFormUserInputFields>) => void;
  /** Set the current step */
  setCurrentStep: (step: GisIntakeStep) => void;
  /** Store the server-side session ID */
  setSessionId: (id: number) => void;
  /** Store the confirmed contact info from step 1 */
  setConfirmedContact: (phone: string, email: string) => void;
  /** Store the triage-determined GIS flow type */
  setTriageGisFlowType: (flowType: string) => void;
  /** Store the counseling intent classified from the order's test status */
  setCounselingIntent: (intent: CounselingIntent | null) => void;
  /** Mark the GIS appointment as booked, locking the intake answers */
  setBookingConfirmed: (confirmed: boolean) => void;
  /** Get the complete intake form data */
  getCompleteData: () => CompleteIntakeFormData | null;
  /** Reset all form data */
  resetForm: () => void;
  /** Check if all required fields are filled */
  isFormComplete: () => boolean;
}

const defaultUserInputFields: IntakeFormUserInputFields = {
  hasActiveDisease: null,
  reasonForAppointment: null,
  easeOfUnderstandingInformation: null,
  wantToTalkToGeneticCounselorImmediately: null,
  alreadyScheduledAppointmentWithDoctor: null,
  countryOfResidence: null,
  interpreterNeeded: null,
  interpreterLanguage: null,
  consentAcknowledged: null,
};

/**
 * Per–order/test cache that survives provider remounts during in-app navigation
 * (e.g. moving between intake steps). Keys must change when starting intake for
 * a different test so answers and session pointers do not leak across tests.
 */
interface FormStateCache {
  userInputFields: IntakeFormUserInputFields;
  sessionId: number | null;
  confirmedPhone: string | null;
  confirmedEmail: string | null;
  triageGisFlowType: string | null;
  counselingIntent: CounselingIntent | null;
  currentStep: GisIntakeStep;
  prefilledData: IntakeFormData | null;
  bookingConfirmed: boolean;
}

const STORAGE_PREFIX = "gis_intake_cache:";

export function getGisIntakeFormStateCacheKey(
  orderUidPublic?: string | null,
  testUid?: string | null
): string {
  return `gis:${orderUidPublic ?? ""}:${testUid ?? ""}`;
}

function readCacheFromStorage(key: string): FormStateCache | undefined {
  try {
    const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${key}`);
    if (raw) return JSON.parse(raw) as FormStateCache;
  } catch {
    // Ignore parse errors or unavailable sessionStorage
  }
  return undefined;
}

function writeCacheToStorage(key: string, cache: FormStateCache): void {
  try {
    sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, JSON.stringify(cache));
  } catch {
    // Ignore quota/unavailability errors
  }
}

function removeCacheFromStorage(key: string): void {
  try {
    sessionStorage.removeItem(`${STORAGE_PREFIX}${key}`);
  } catch {
    // Ignore
  }
}

const formStateCaches = new Map<string, FormStateCache>();

/** @visibleForTesting — reset all cached buckets between test runs */
export function resetFormStateCache(): void {
  formStateCaches.clear();
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(STORAGE_PREFIX)) keysToRemove.push(k);
    }
    keysToRemove.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // Ignore if sessionStorage is unavailable
  }
}

/**
 * Drop a single cached form-state bucket from both the in-memory map and
 * sessionStorage. Used when a funnel starts a *fresh* intake for a cache key it
 * may have used before (notably the guest no-order flow, whose cache key is a
 * static sentinel): without this, a prior run's `bookingConfirmed` flag would
 * rehydrate and bounce the visitor out of the intake steps to a terminal
 * destination.
 */
export function clearFormStateCache(key: string): void {
  formStateCaches.delete(key);
  removeCacheFromStorage(key);
}

function getFormStateCache(key: string): FormStateCache | undefined {
  return formStateCaches.get(key) ?? readCacheFromStorage(key);
}

function syncFormStateCache(key: string, patch: Partial<FormStateCache>): void {
  const existing = formStateCaches.get(key) ?? readCacheFromStorage(key);
  if (existing) {
    Object.assign(existing, patch);
    formStateCaches.set(key, existing);
  } else {
    const entry: FormStateCache = {
      userInputFields: { ...defaultUserInputFields },
      sessionId: null,
      confirmedPhone: null,
      confirmedEmail: null,
      triageGisFlowType: null,
      counselingIntent: null,
      currentStep: GisIntakeStep.CONFIRM_INFO,
      prefilledData: null,
      bookingConfirmed: false,
      ...patch,
    };
    formStateCaches.set(key, entry);
  }
  const cache = formStateCaches.get(key)!;
  writeCacheToStorage(key, cache);
}

const IntakeFormContext = createContext<IntakeFormContextValue | undefined>(
  undefined
);

interface IntakeFormProviderProps {
  children: ReactNode;
  /** Bucket id — use a stable string per order+test in the GIS flow */
  stateCacheKey?: string;
  initialPrefilledData?: IntakeFormData | null;
  initialUserInputFields?: Partial<IntakeFormUserInputFields>;
  /**
   * When true, the no-order ("no test found") step sequence is used, which
   * inserts the select-test / cancer-type / cancer-stage steps that collect
   * data a backing order would otherwise pre-fill.
   */
  isNoOrder?: boolean;
  /** Optional flow-specific sequence, used by guest intake to add guest-only steps. */
  stepSequence?: GisIntakeStep[];
}

export const IntakeFormProvider: FC<IntakeFormProviderProps> = ({
  children,
  stateCacheKey = "default",
  initialPrefilledData = null,
  initialUserInputFields = {},
  isNoOrder = false,
  stepSequence,
}) => {
  const cached = getFormStateCache(stateCacheKey);
  const [prefilledData, setPrefilledDataState] =
    useState<IntakeFormData | null>(
      cached?.prefilledData ?? initialPrefilledData
    );
  const [userInputFields, setUserInputFields] =
    useState<IntakeFormUserInputFields>(
      cached?.userInputFields ?? {
        ...defaultUserInputFields,
        ...initialUserInputFields,
      }
    );
  const [currentStep, setCurrentStepState] = useState<GisIntakeStep>(
    cached?.currentStep ?? GisIntakeStep.CONFIRM_INFO
  );
  const [sessionId, setSessionIdState] = useState<number | null>(
    cached?.sessionId ?? null
  );
  const [confirmedPhone, setConfirmedPhoneState] = useState<string | null>(
    cached?.confirmedPhone ?? null
  );
  const [confirmedEmail, setConfirmedEmailState] = useState<string | null>(
    cached?.confirmedEmail ?? null
  );
  const [triageGisFlowType, setTriageGisFlowTypeState] = useState<
    string | null
  >(cached?.triageGisFlowType ?? null);
  const [counselingIntent, setCounselingIntentState] =
    useState<CounselingIntent | null>(cached?.counselingIntent ?? null);
  const [bookingConfirmed, setBookingConfirmedState] = useState<boolean>(
    cached?.bookingConfirmed ?? false
  );
  const steps =
    stepSequence ?? (isNoOrder ? GIS_INTAKE_STEPS_NO_ORDER : GIS_INTAKE_STEPS);
  const totalSteps = steps.length;

  useEffect(() => {
    const nextCached = getFormStateCache(stateCacheKey);
    setUserInputFields(
      nextCached?.userInputFields ?? {
        ...defaultUserInputFields,
        ...initialUserInputFields,
      }
    );
    setCurrentStepState(nextCached?.currentStep ?? GisIntakeStep.CONFIRM_INFO);
    setSessionIdState(nextCached?.sessionId ?? null);
    setConfirmedPhoneState(nextCached?.confirmedPhone ?? null);
    setConfirmedEmailState(nextCached?.confirmedEmail ?? null);
    setTriageGisFlowTypeState(nextCached?.triageGisFlowType ?? null);
    setCounselingIntentState(nextCached?.counselingIntent ?? null);
    setBookingConfirmedState(nextCached?.bookingConfirmed ?? false);
    setPrefilledDataState(
      nextCached?.prefilledData ?? initialPrefilledData ?? null
    );
    // Rehydrate only when `stateCacheKey` changes. Do not list
    // initialPrefilledData / initialUserInputFields: the default `{}` for the
    // latter is a new object each render when omitted, which would re-run this
    // effect every render and wipe in-progress intake state.
  }, [stateCacheKey]);

  const setPrefilledData = useCallback(
    (data: IntakeFormData | null) => {
      setPrefilledDataState(data);
      syncFormStateCache(stateCacheKey, { prefilledData: data });
    },
    [stateCacheKey]
  );

  const updatePrefilledField = useCallback(
    <K extends keyof IntakeFormData>(field: K, value: IntakeFormData[K]) => {
      setPrefilledDataState((prev) => {
        const base: IntakeFormData = prev ?? {
          firstName: null,
          lastName: null,
          dateOfBirth: null,
          phone: null,
          email: null,
          physicianClinic: null,
          orderingPhysician: null,
          cancerType: null,
          cancerStage: null,
          testType: null,
          hasGISAppointment: null,
          hasPreviouslyTakenTest: null,
        };
        const next = { ...base, [field]: value };
        syncFormStateCache(stateCacheKey, { prefilledData: next });
        return next;
      });
    },
    [stateCacheKey]
  );

  const getStepNumber = useCallback(
    (step: GisIntakeStep) => getStepNumberForSteps(step, steps),
    [steps]
  );

  const getNextStep = useCallback(
    (step: GisIntakeStep) => getNextStepForSteps(step, steps),
    [steps]
  );

  const getPreviousStep = useCallback(
    (step: GisIntakeStep) => getPreviousStepForSteps(step, steps),
    [steps]
  );

  const setCurrentStep = useCallback(
    (step: GisIntakeStep) => {
      setCurrentStepState(step);
      syncFormStateCache(stateCacheKey, { currentStep: step });
    },
    [stateCacheKey]
  );

  const setSessionId = useCallback(
    (id: number) => {
      setSessionIdState(id);
      syncFormStateCache(stateCacheKey, { sessionId: id });
    },
    [stateCacheKey]
  );

  const setConfirmedContact = useCallback(
    (phone: string, email: string) => {
      setConfirmedPhoneState(phone);
      setConfirmedEmailState(email);
      syncFormStateCache(stateCacheKey, {
        confirmedPhone: phone,
        confirmedEmail: email,
      });
    },
    [stateCacheKey]
  );

  const setTriageGisFlowType = useCallback(
    (flowType: string) => {
      setTriageGisFlowTypeState(flowType);
      syncFormStateCache(stateCacheKey, { triageGisFlowType: flowType });
    },
    [stateCacheKey]
  );

  const setCounselingIntent = useCallback(
    (intent: CounselingIntent | null) => {
      setCounselingIntentState((prev) => {
        if (prev === intent) return prev;
        syncFormStateCache(stateCacheKey, { counselingIntent: intent });
        return intent;
      });
    },
    [stateCacheKey]
  );

  const setBookingConfirmed = useCallback(
    (confirmed: boolean) => {
      setBookingConfirmedState(confirmed);
      syncFormStateCache(stateCacheKey, { bookingConfirmed: confirmed });
    },
    [stateCacheKey]
  );

  const updateUserField = useCallback(
    <K extends keyof IntakeFormUserInputFields>(
      field: K,
      value: IntakeFormUserInputFields[K]
    ) => {
      setUserInputFields((prev) => {
        const next = { ...prev, [field]: value };
        syncFormStateCache(stateCacheKey, { userInputFields: next });
        return next;
      });
    },
    [stateCacheKey]
  );

  const updateUserFields = useCallback(
    (fields: Partial<IntakeFormUserInputFields>) => {
      setUserInputFields((prev) => {
        const next = { ...prev, ...fields };
        syncFormStateCache(stateCacheKey, { userInputFields: next });
        return next;
      });
    },
    [stateCacheKey]
  );

  const getCompleteData = useCallback((): CompleteIntakeFormData | null => {
    if (!prefilledData) return null;
    return {
      ...prefilledData,
      ...userInputFields,
    };
  }, [prefilledData, userInputFields]);

  const resetForm = useCallback(() => {
    setUserInputFields(defaultUserInputFields);
    setCurrentStepState(GisIntakeStep.CONFIRM_INFO);
    setSessionIdState(null);
    setConfirmedPhoneState(null);
    setConfirmedEmailState(null);
    setTriageGisFlowTypeState(null);
    setCounselingIntentState(null);
    setBookingConfirmedState(false);
    setPrefilledDataState(null);
    formStateCaches.delete(stateCacheKey);
    removeCacheFromStorage(stateCacheKey);
  }, [stateCacheKey]);

  const isFormComplete = useCallback((): boolean => {
    const {
      hasActiveDisease,
      reasonForAppointment,
      easeOfUnderstandingInformation,
      wantToTalkToGeneticCounselorImmediately,
      alreadyScheduledAppointmentWithDoctor,
      countryOfResidence,
      interpreterNeeded,
      interpreterLanguage,
      consentAcknowledged,
    } = userInputFields;

    const basicFieldsFilled =
      hasActiveDisease !== null &&
      reasonForAppointment !== null &&
      easeOfUnderstandingInformation !== null &&
      wantToTalkToGeneticCounselorImmediately !== null &&
      alreadyScheduledAppointmentWithDoctor !== null &&
      countryOfResidence !== null &&
      interpreterNeeded !== null &&
      consentAcknowledged === true;

    if (interpreterNeeded && !interpreterLanguage) {
      return false;
    }

    return basicFieldsFilled;
  }, [userInputFields]);

  const value: IntakeFormContextValue = {
    prefilledData,
    userInputFields,
    currentStep,
    steps,
    totalSteps,
    sessionId,
    confirmedPhone,
    confirmedEmail,
    triageGisFlowType,
    counselingIntent,
    bookingConfirmed,
    setPrefilledData,
    updatePrefilledField,
    getStepNumber,
    getNextStep,
    getPreviousStep,
    updateUserField,
    updateUserFields,
    setCurrentStep,
    setSessionId,
    setConfirmedContact,
    setTriageGisFlowType,
    setCounselingIntent,
    setBookingConfirmed,
    getCompleteData,
    resetForm,
    isFormComplete,
  };

  return (
    <IntakeFormContext.Provider value={value}>
      {children}
    </IntakeFormContext.Provider>
  );
};

export const useIntakeForm = (): IntakeFormContextValue => {
  const context = useContext(IntakeFormContext);
  if (!context) {
    throw new Error("useIntakeForm must be used within IntakeFormProvider");
  }
  return context;
};

export default IntakeFormContext;
